import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../api';
import Swal from 'sweetalert2';
import mammoth from 'mammoth';
import './MetadataLibrary.css';

/* ─── helpers ─────────────────────────────────────────── */
const PAGE_SIZE = 15;

function ConfirmDeleteModal({ item, label, onConfirm, onCancel }) {
  const { t } = useTranslation();
  return (
    <div className="ml-overlay" onClick={onCancel}>
      <div className="ml-modal ml-modal-sm" onClick={e => e.stopPropagation()}>
        <div className="ml-modal-header">
          <span>{t('MetadataLibrary.deleteEntity', { label })}</span>
          <button className="ml-modal-close" onClick={onCancel}><i className="fas fa-times" /></button>
        </div>
        <div className="ml-modal-body">
          <p className="ml-confirm-text">
            {t('MetadataLibrary.confirmDeletePrefix')} <strong>{item?.name || item?.title || item?.policyName || label}</strong>{t('MetadataLibrary.confirmDeleteSuffix')}
          </p>
        </div>
        <div className="ml-modal-footer">
          <button className="ml-btn-secondary" onClick={onCancel}>{t('MetadataLibrary.cancel')}</button>
          <button className="ml-btn-danger" onClick={onConfirm}>
            <i className="fas fa-trash-alt" /> {t('MetadataLibrary.delete')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   FRAMEWORKS TAB
═══════════════════════════════════════════════════════ */
function FrameworksTab() {
  const { t } = useTranslation();
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [modal, setModal]         = useState(null); // null | { mode:'add'|'edit', item }
  const [delTarget, setDelTarget] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/frameworks');
      setRows(res.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm({ name:'', standard:'', description:'', displayName:'', shortName:'' }); setModal({ mode:'add' }); };
  const openEdit = (item) => { setForm({ ...item }); setModal({ mode:'edit', item }); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'add') {
        // POST uses the bulk-format endpoint; we'll use PUT approach for admin simple create
        await axiosInstance.post('/frameworks', {
          data: { organization: { allFrameworks: [{
            standard: form.standard,
            standardInfo: { description: form.description, displayName: form.displayName, shortName: form.shortName }
          }] } }
        });
      } else {
        await axiosInstance.put(`/frameworks/${modal.item.id}`, form);
      }
      setModal(null);
      load();
    } catch (err) {
      Swal.fire(t('MetadataLibrary.error'), err.response?.data?.error || err.message, 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/frameworks/${delTarget.id}`);
      setDelTarget(null);
      load();
    } catch (err) {
      setDelTarget(null);
      Swal.fire(t('MetadataLibrary.cannotDelete'), err.response?.data?.error || err.message, 'error');
    }
  };

  const filtered = rows.filter(r =>
    [r.displayName, r.name, r.standard, r.shortName].some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div>
      <div className="ml-toolbar">
        <div className="ml-search-wrap">
          <i className="fas fa-search ml-search-icon" />
          <input className="ml-search" placeholder={t('MetadataLibrary.searchFrameworks')} value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="ml-btn-primary" onClick={openAdd}>
          <i className="fas fa-plus" /> {t('MetadataLibrary.addFramework')}
        </button>
      </div>

      {loading ? <div className="ml-loading"><div className="ml-spinner" /></div> : (
        <div className="ml-card">
          <table className="ml-table">
            <thead><tr>
              <th>{t('MetadataLibrary.displayName')}</th><th>{t('MetadataLibrary.standard')}</th><th>{t('MetadataLibrary.shortName')}</th><th>{t('MetadataLibrary.description')}</th><th className="ml-th-actions">{t('MetadataLibrary.actions')}</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={5} className="ml-empty-row">{t('MetadataLibrary.noFrameworksFound')}</td></tr>}
              {filtered.map(r => (
                <tr key={r.id} className="ml-row">
                  <td><strong>{r.displayName || r.name}</strong></td>
                  <td>{r.standard}</td>
                  <td><span className="ml-badge ml-badge-blue">{r.shortName}</span></td>
                  <td className="ml-desc-cell">{r.description}</td>
                  <td className="ml-actions-cell">
                    <button className="ml-icon-btn ml-icon-edit" onClick={() => openEdit(r)} title={t('MetadataLibrary.edit')}><i className="fas fa-pencil-alt" /></button>
                    <button className="ml-icon-btn ml-icon-del"  onClick={() => setDelTarget(r)} title={t('MetadataLibrary.delete')}><i className="fas fa-trash-alt" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="ml-overlay" onClick={() => setModal(null)}>
          <div className="ml-modal" onClick={e => e.stopPropagation()}>
            <div className="ml-modal-header">
              <span>{modal.mode === 'add' ? t('MetadataLibrary.addFramework') : t('MetadataLibrary.editFramework')}</span>
              <button className="ml-modal-close" onClick={() => setModal(null)}><i className="fas fa-times" /></button>
            </div>
            <div className="ml-modal-body">
              <div className="ml-grid-2">
                {[['displayName',t('MetadataLibrary.displayName')],['name',t('MetadataLibrary.internalName')],['standard',t('MetadataLibrary.standard')],['shortName',t('MetadataLibrary.shortName')]].map(([k,l]) => (
                  <div className="ml-field" key={k}>
                    <label>{l}</label>
                    <input className="ml-input" value={form[k] || ''} onChange={e => setForm(f => ({...f, [k]: e.target.value}))} />
                  </div>
                ))}
              </div>
              <div className="ml-field" style={{marginTop:12}}>
                <label>{t('MetadataLibrary.description')}</label>
                <textarea className="ml-input ml-textarea" rows={3}
                  value={form.description || ''} onChange={e => setForm(f => ({...f, description: e.target.value}))} />
              </div>
            </div>
            <div className="ml-modal-footer">
              <button className="ml-btn-secondary" onClick={() => setModal(null)}>{t('MetadataLibrary.cancel')}</button>
              <button className="ml-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><i className="fas fa-spinner fa-spin" /> {t('MetadataLibrary.saving')}</> : <><i className="fas fa-check" /> {t('MetadataLibrary.save')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {delTarget && (
        <ConfirmDeleteModal item={delTarget} label={t('MetadataLibrary.framework')}
          onConfirm={handleDelete} onCancel={() => setDelTarget(null)} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   CONTROLS TAB
═══════════════════════════════════════════════════════ */
function ControlsTab() {
  const { t } = useTranslation();
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [modal, setModal]         = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState({});

  const load = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, size: PAGE_SIZE });
      if (search) params.set('controlName', search);
      const res = await axiosInstance.get(`/requirement-subcategory/controls?${params}`);
      setRows(res.data || []);
      setTotal(parseInt(res.headers['x-total-count'] || '0', 10));
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { setPage(0); load(0); }, [search, load]);
  useEffect(() => { load(page); }, [page, load]);

  const openEdit = (item) => {
    setForm({ name: item.name, description: item.description, key: item.key, controlId: item.controlId });
    setModal({ mode:'edit', item });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/requirement-subcategory/${modal.item.id}/controls`, form);
      setModal(null);
      load(page);
    } catch (err) {
      Swal.fire(t('MetadataLibrary.error'), err.response?.data?.error || err.message, 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/requirement-subcategory/${delTarget.id}/controls`);
      setDelTarget(null);
      load(page);
    } catch (err) {
      setDelTarget(null);
      Swal.fire(t('MetadataLibrary.error'), err.response?.data?.error || err.message, 'error');
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="ml-toolbar">
        <div className="ml-search-wrap">
          <i className="fas fa-search ml-search-icon" />
          <input className="ml-search" placeholder={t('MetadataLibrary.searchControls')} value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
      </div>
      <div className="ml-info-notice">
        <i className="fas fa-info-circle" /> {t('MetadataLibrary.controlsCascadeNotice')}
      </div>

      {loading ? <div className="ml-loading"><div className="ml-spinner" /></div> : (
        <div className="ml-card">
          <table className="ml-table">
            <thead><tr>
              <th>{t('MetadataLibrary.controlId')}</th><th>{t('MetadataLibrary.name')}</th><th>{t('MetadataLibrary.key')}</th><th>{t('MetadataLibrary.frameworks')}</th><th className="ml-th-actions">{t('MetadataLibrary.actions')}</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="ml-empty-row">{t('MetadataLibrary.noControlsFound')}</td></tr>}
              {rows.map((r, i) => (
                <tr key={r.id || r.controlId || i} className="ml-row">
                  <td><span className="ml-mono">{r.controlId}</span></td>
                  <td>{r.name}</td>
                  <td><span className="ml-badge ml-badge-gray">{r.key}</span></td>
                  <td>
                    {(r.framework || []).slice(0, 3).map(fw => (
                      <span key={fw.displayName} className="ml-badge ml-badge-blue" style={{marginRight:3}}>{fw.shortName || fw.displayName}</span>
                    ))}
                    {(r.framework || []).length > 3 && <span className="ml-badge ml-badge-gray">+{r.framework.length - 3}</span>}
                  </td>
                  <td className="ml-actions-cell">
                    <button className="ml-icon-btn ml-icon-edit" onClick={() => openEdit(r)} title={t('MetadataLibrary.edit')}><i className="fas fa-pencil-alt" /></button>
                    <button className="ml-icon-btn ml-icon-del"  onClick={() => setDelTarget(r)} title={t('MetadataLibrary.delete')}><i className="fas fa-trash-alt" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="ml-pagination">
              <button className="ml-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                <i className="fas fa-chevron-left" />
              </button>
              <span className="ml-page-info">{t('MetadataLibrary.pageOf', { current: page + 1, total: totalPages })}</span>
              <button className="ml-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                <i className="fas fa-chevron-right" />
              </button>
            </div>
          )}
        </div>
      )}

      {modal && (
        <div className="ml-overlay" onClick={() => setModal(null)}>
          <div className="ml-modal" onClick={e => e.stopPropagation()}>
            <div className="ml-modal-header">
              <span>{t('MetadataLibrary.editControl')}</span>
              <button className="ml-modal-close" onClick={() => setModal(null)}><i className="fas fa-times" /></button>
            </div>
            <div className="ml-modal-body">
              <div className="ml-grid-2">
                {[['name',t('MetadataLibrary.name')],['key',t('MetadataLibrary.key')],['controlId',t('MetadataLibrary.controlId')]].map(([k,l]) => (
                  <div className="ml-field" key={k}>
                    <label>{l}</label>
                    <input className="ml-input" value={form[k] || ''} onChange={e => setForm(f => ({...f, [k]: e.target.value}))} />
                  </div>
                ))}
              </div>
              <div className="ml-field" style={{marginTop:12}}>
                <label>{t('MetadataLibrary.description')}</label>
                <textarea className="ml-input ml-textarea" rows={3}
                  value={form.description || ''} onChange={e => setForm(f => ({...f, description: e.target.value}))} />
              </div>
            </div>
            <div className="ml-modal-footer">
              <button className="ml-btn-secondary" onClick={() => setModal(null)}>{t('MetadataLibrary.cancel')}</button>
              <button className="ml-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><i className="fas fa-spinner fa-spin" /> {t('MetadataLibrary.saving')}</> : <><i className="fas fa-check" /> {t('MetadataLibrary.save')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {delTarget && (
        <ConfirmDeleteModal item={delTarget} label={t('MetadataLibrary.control')}
          onConfirm={handleDelete} onCancel={() => setDelTarget(null)} />
      )}
    </div>
  );
}

/* ── category helpers ──────────────────────────────── */
const BASE_CATEGORIES = [
  'Engineering', 'Human Resources', 'Information Technology', 'Policy',
  'Risks', 'Access Control', 'Encryption', 'Logging', 'Vulnerability',
  'Configuration', 'Network', 'Identity', 'Physical', 'Compliance',
];

function parseCats(raw) {
  if (!raw) return [];
  return raw.split(',').map(s => s.trim()).filter(Boolean);
}

function CategoryPicker({ selected, onChange, knownCats }) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const allOptions = [...new Set([...BASE_CATEGORIES, ...knownCats])].sort();
  const suggestions = input.trim()
    ? allOptions.filter(c => c.toLowerCase().includes(input.toLowerCase()) && !selected.includes(c))
    : allOptions.filter(c => !selected.includes(c));

  const add = (cat) => {
    const trimmed = cat.trim();
    if (trimmed && !selected.includes(trimmed)) onChange([...selected, trimmed]);
    setInput('');
  };
  const remove = (cat) => onChange(selected.filter(c => c !== cat));

  const handleKey = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
      e.preventDefault();
      add(input);
    }
    if (e.key === 'Backspace' && !input && selected.length) {
      remove(selected[selected.length - 1]);
    }
  };

  return (
    <div className="ml-cat-picker">
      <div className="ml-cat-tags-box">
        {selected.map(cat => (
          <span key={cat} className="ml-cat-tag">
            {cat}
            <button type="button" className="ml-cat-tag-remove" onClick={() => remove(cat)}>
              <i className="fas fa-times" />
            </button>
          </span>
        ))}
        <input
          className="ml-cat-input"
          placeholder={selected.length === 0 ? t('MetadataLibrary.typeOrSelectCategories') : t('MetadataLibrary.addMore')}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
        />
      </div>
      {suggestions.length > 0 && (
        <div className="ml-cat-suggestions">
          {suggestions.slice(0, 8).map(c => (
            <button key={c} type="button" className="ml-cat-suggestion" onClick={() => add(c)}>
              {c}
            </button>
          ))}
          {input.trim() && !allOptions.some(c => c.toLowerCase() === input.trim().toLowerCase()) && (
            <button type="button" className="ml-cat-suggestion ml-cat-suggestion-new" onClick={() => add(input)}>
              <i className="fas fa-plus" /> {t('MetadataLibrary.addQuoted', { value: input.trim() })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   TESTS TAB
═══════════════════════════════════════════════════════ */
function TestsTab() {
  const { t } = useTranslation();
  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [modal, setModal]         = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState({ name: '', categories: [] });

  // collect all categories seen in loaded rows for suggestions
  const knownCats = [...new Set(
    rows.flatMap(r => parseCats(r.testCategory))
  )];

  const load = useCallback(async (p = 0) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, size: PAGE_SIZE });
      if (search) params.set('testName', search);
      const res = await axiosInstance.get(`/tests?${params}`);
      setRows(res.data || []);
      setTotal(parseInt(res.headers['x-total-count'] || '0', 10));
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { setPage(0); load(0); }, [search, load]);
  useEffect(() => { load(page); }, [page, load]);

  const openAdd  = () => { setForm({ name: '', categories: [] }); setModal({ mode: 'add' }); };
  const openEdit = (item) => {
    setForm({ name: item.testName, categories: parseCats(item.testCategory) });
    setModal({ mode: 'edit', item });
  };

  const handleSave = async () => {
    if (!form.name?.trim()) return Swal.fire(t('MetadataLibrary.validation'), t('MetadataLibrary.testNameRequired'), 'warning');
    const categoryStr = form.categories.join(', ');
    setSaving(true);
    try {
      if (modal.mode === 'add') {
        await axiosInstance.post('/tests', [{ node: { name: form.name, category: categoryStr, controls: [] } }]);
      } else {
        await axiosInstance.put(`/tests/${modal.item.id}`, { name: form.name, testCategory: categoryStr });
      }
      setModal(null);
      load(page);
    } catch (err) {
      Swal.fire(t('MetadataLibrary.error'), err.response?.data?.error || err.message, 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/tests/${delTarget.id}`);
      setDelTarget(null);
      load(page);
    } catch (err) {
      setDelTarget(null);
      Swal.fire(t('MetadataLibrary.error'), err.message, 'error');
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="ml-toolbar">
        <div className="ml-search-wrap">
          <i className="fas fa-search ml-search-icon" />
          <input className="ml-search" placeholder={t('MetadataLibrary.searchTests')} value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="ml-btn-primary" onClick={openAdd}>
          <i className="fas fa-plus" /> {t('MetadataLibrary.addTest')}
        </button>
      </div>

      {loading ? <div className="ml-loading"><div className="ml-spinner" /></div> : (
        <div className="ml-card">
          <table className="ml-table">
            <thead><tr>
              <th>{t('MetadataLibrary.name')}</th><th>{t('MetadataLibrary.categories')}</th><th>{t('MetadataLibrary.frameworks')}</th><th className="ml-th-actions">{t('MetadataLibrary.actions')}</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="ml-empty-row">{t('MetadataLibrary.noTestsFound')}</td></tr>}
              {rows.map(r => {
                const cats = parseCats(r.testCategory);
                return (
                  <tr key={r.id} className="ml-row">
                    <td>{r.testName}</td>
                    <td>
                      {cats.length === 0
                        ? <span className="ml-muted">—</span>
                        : cats.map(c => (
                          <span key={c} className="ml-badge ml-badge-gray" style={{ marginRight: 3 }}>{c}</span>
                        ))
                      }
                    </td>
                    <td>
                      {(r.frameworkResponseDTOS || []).slice(0, 3).map(fw => (
                        <span key={fw.displayName} className="ml-badge ml-badge-blue" style={{ marginRight: 3 }}>{fw.shortName || fw.displayName}</span>
                      ))}
                      {(r.frameworkResponseDTOS || []).length > 3 && (
                        <span className="ml-badge ml-badge-gray">+{r.frameworkResponseDTOS.length - 3}</span>
                      )}
                    </td>
                    <td className="ml-actions-cell">
                      <button className="ml-icon-btn ml-icon-edit" onClick={() => openEdit(r)} title={t('MetadataLibrary.edit')}><i className="fas fa-pencil-alt" /></button>
                      <button className="ml-icon-btn ml-icon-del"  onClick={() => setDelTarget(r)} title={t('MetadataLibrary.delete')}><i className="fas fa-trash-alt" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="ml-pagination">
              <button className="ml-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                <i className="fas fa-chevron-left" />
              </button>
              <span className="ml-page-info">{t('MetadataLibrary.pageOf', { current: page + 1, total: totalPages })}</span>
              <button className="ml-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                <i className="fas fa-chevron-right" />
              </button>
            </div>
          )}
        </div>
      )}

      {modal && (
        <div className="ml-overlay" onClick={() => setModal(null)}>
          <div className="ml-modal" onClick={e => e.stopPropagation()}>
            <div className="ml-modal-header">
              <span>{modal.mode === 'add' ? t('MetadataLibrary.addTest') : t('MetadataLibrary.editTest')}</span>
              <button className="ml-modal-close" onClick={() => setModal(null)}><i className="fas fa-times" /></button>
            </div>
            <div className="ml-modal-body">
              <div className="ml-field" style={{ marginBottom: 14 }}>
                <label>{t('MetadataLibrary.testName')} <span className="ml-req">*</span></label>
                <input className="ml-input" value={form.name || ''}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="ml-field">
                <label>{t('MetadataLibrary.categories')}</label>
                <p className="ml-field-note" style={{ marginBottom: 6 }}>
                  {t('MetadataLibrary.categoriesNote')}
                </p>
                <CategoryPicker
                  selected={form.categories}
                  onChange={cats => setForm(f => ({ ...f, categories: cats }))}
                  knownCats={knownCats}
                />
              </div>
            </div>
            <div className="ml-modal-footer">
              <button className="ml-btn-secondary" onClick={() => setModal(null)}>{t('MetadataLibrary.cancel')}</button>
              <button className="ml-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><i className="fas fa-spinner fa-spin" /> {t('MetadataLibrary.saving')}</> : <><i className="fas fa-check" /> {t('MetadataLibrary.save')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {delTarget && (
        <ConfirmDeleteModal item={{ name: delTarget.testName }} label={t('MetadataLibrary.test')}
          onConfirm={handleDelete} onCancel={() => setDelTarget(null)} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   POLICIES TAB
═══════════════════════════════════════════════════════ */
function PoliciesTab() {
  const { t } = useTranslation();
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [delTarget, setDelTarget] = useState(null);
  const [converting, setConverting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Meta Library manages MASTER policy templates only. /policies returns every policy
      // (incl. org-owned, non-template copies); /policies/templates is the master set.
      const res = await axiosInstance.get('/policies/templates');
      setRows(res.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/policies/${delTarget.policyId}`);
      setDelTarget(null);
      load();
    } catch (err) {
      setDelTarget(null);
      Swal.fire(t('MetadataLibrary.error'), err.message, 'error');
    }
  };

  // T-0389 Phase 3: templates shipped in Phases 1-2 with NULL templateHtml render no in-app
  // editor for any org copy (OrganizationPolicyService.renderContentHtml no-ops when blank).
  // Converts this template's existing .docx (mammoth, same library/approach as the org
  // upload-a-new-policy flow in policyUtils.js) and persists it via the already-existing
  // platform_admin-only PUT /policies/{id}/template endpoint.
  const handleConvertToHtml = async (policy) => {
    setConverting(policy.policyId);
    try {
      const fileRes = await axiosInstance.get(`/policies/${policy.policyId}/download`, {
        responseType: 'blob',
      });
      const arrayBuffer = await fileRes.data.arrayBuffer();
      const { value: templateHtml } = await mammoth.convertToHtml({ arrayBuffer });
      await axiosInstance.put(`/policies/${policy.policyId}/template`, { templateHtml });
      await load();
    } catch (err) {
      Swal.fire(t('MetadataLibrary.error'), err.response?.data?.message || err.message || t('MetadataLibrary.alertConvertFailed'), 'error');
    } finally {
      setConverting(null);
    }
  };

  const filtered = rows.filter(r =>
    r.policyName?.toLowerCase().includes(search.toLowerCase()) ||
    r.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="ml-toolbar">
        <div className="ml-search-wrap">
          <i className="fas fa-search ml-search-icon" />
          <input className="ml-search" placeholder={t('MetadataLibrary.searchPolicies')} value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="ml-note">
          <i className="fas fa-info-circle" /> {t('MetadataLibrary.policiesNote')}
        </div>
      </div>

      {loading ? <div className="ml-loading"><div className="ml-spinner" /></div> : (
        <div className="ml-card">
          <table className="ml-table">
            <thead><tr>
              <th>{t('MetadataLibrary.name')}</th><th>{t('MetadataLibrary.description')}</th><th>{t('MetadataLibrary.file')}</th><th>{t('MetadataLibrary.htmlStatus')}</th><th>{t('MetadataLibrary.controls')}</th><th>{t('MetadataLibrary.frameworks')}</th><th className="ml-th-actions">{t('MetadataLibrary.actions')}</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={7} className="ml-empty-row">{t('MetadataLibrary.noPoliciesFound')}</td></tr>}
              {filtered.map(r => (
                <tr key={r.policyId} className="ml-row">
                  <td><strong>{r.policyName}</strong></td>
                  <td className="ml-desc-cell">{r.description}</td>
                  <td>
                    {r.policyLink
                      ? <span className="ml-mono ml-file-chip"><i className="fas fa-file-alt" /> {r.policyFileName || t('MetadataLibrary.fileFallback')}</span>
                      : <span className="ml-muted">—</span>}
                  </td>
                  <td>
                    {r.hasHtmlTemplate
                      ? <span className="ml-badge ml-badge-green">{t('MetadataLibrary.htmlReady')}</span>
                      : <span className="ml-badge ml-badge-orange">{t('MetadataLibrary.docxOnly')}</span>}
                  </td>
                  <td><span className="ml-badge ml-badge-gray">{t('MetadataLibrary.ctrlCount', { count: (r.controlIds || []).length })}</span></td>
                  <td><span className="ml-badge ml-badge-blue">{t('MetadataLibrary.fwCount', { count: (r.frameworkIds || []).length })}</span></td>
                  <td className="ml-actions-cell">
                    {!r.hasHtmlTemplate && r.policyLink && (
                      <button className="ml-icon-btn ml-icon-edit" onClick={() => handleConvertToHtml(r)}
                        disabled={converting === r.policyId} title={t('MetadataLibrary.convertToHtml')}>
                        {converting === r.policyId
                          ? <i className="fas fa-spinner fa-spin" />
                          : <i className="fas fa-file-export" />}
                      </button>
                    )}
                    <button className="ml-icon-btn ml-icon-del" onClick={() => setDelTarget(r)} title={t('MetadataLibrary.delete')}>
                      <i className="fas fa-trash-alt" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {delTarget && (
        <ConfirmDeleteModal item={{ name: delTarget.policyName }} label={t('MetadataLibrary.policy')}
          onConfirm={handleDelete} onCancel={() => setDelTarget(null)} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   DOCUMENTS TAB
═══════════════════════════════════════════════════════ */
function DocumentsTab() {
  const { t } = useTranslation();
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [modal, setModal]         = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/documents');
      setRows(res.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (item) => {
    setForm({ title: item.title, description: item.description, category: item.category, documentId: item.documentId });
    setModal({ mode:'edit', item });
  };

  const handleSave = async () => {
    if (!form.title?.trim()) return Swal.fire(t('MetadataLibrary.validation'), t('MetadataLibrary.titleRequired'), 'warning');
    setSaving(true);
    try {
      await axiosInstance.put(`/documents/${modal.item.id}`, form);
      setModal(null);
      load();
    } catch (err) {
      Swal.fire(t('MetadataLibrary.error'), err.response?.data?.error || err.message, 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/documents/${delTarget.id}`);
      setDelTarget(null);
      load();
    } catch (err) {
      setDelTarget(null);
      Swal.fire(t('MetadataLibrary.error'), err.message, 'error');
    }
  };

  const filtered = rows.filter(r =>
    [r.title, r.category, r.documentId].some(v => v?.toLowerCase().includes(search.toLowerCase()))
  );

  const CATEGORY_COLORS = {
    'POLICY':    'ml-badge-purple',
    'PROCEDURE': 'ml-badge-green',
    'STANDARD':  'ml-badge-blue',
    'GUIDELINE': 'ml-badge-orange',
  };

  return (
    <div>
      <div className="ml-toolbar">
        <div className="ml-search-wrap">
          <i className="fas fa-search ml-search-icon" />
          <input className="ml-search" placeholder={t('MetadataLibrary.searchDocuments')} value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="ml-note">
          <i className="fas fa-info-circle" /> {t('MetadataLibrary.documentsNote')}
        </div>
      </div>

      {loading ? <div className="ml-loading"><div className="ml-spinner" /></div> : (
        <div className="ml-card">
          <table className="ml-table">
            <thead><tr>
              <th>{t('MetadataLibrary.title')}</th><th>{t('MetadataLibrary.category')}</th><th>{t('MetadataLibrary.docId')}</th><th>{t('MetadataLibrary.description')}</th><th className="ml-th-actions">{t('MetadataLibrary.actions')}</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={5} className="ml-empty-row">{t('MetadataLibrary.noDocumentsFound')}</td></tr>}
              {filtered.map(r => (
                <tr key={r.id} className="ml-row">
                  <td><strong>{r.title}</strong></td>
                  <td><span className={`ml-badge ${CATEGORY_COLORS[r.category?.toUpperCase()] || 'ml-badge-gray'}`}>{r.category}</span></td>
                  <td><span className="ml-mono">{r.documentId}</span></td>
                  <td className="ml-desc-cell">{r.description}</td>
                  <td className="ml-actions-cell">
                    <button className="ml-icon-btn ml-icon-edit" onClick={() => openEdit(r)} title={t('MetadataLibrary.edit')}><i className="fas fa-pencil-alt" /></button>
                    <button className="ml-icon-btn ml-icon-del"  onClick={() => setDelTarget(r)} title={t('MetadataLibrary.delete')}><i className="fas fa-trash-alt" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="ml-overlay" onClick={() => setModal(null)}>
          <div className="ml-modal" onClick={e => e.stopPropagation()}>
            <div className="ml-modal-header">
              <span>{t('MetadataLibrary.editDocument')}</span>
              <button className="ml-modal-close" onClick={() => setModal(null)}><i className="fas fa-times" /></button>
            </div>
            <div className="ml-modal-body">
              <div className="ml-grid-2">
                <div className="ml-field">
                  <label>{t('MetadataLibrary.title')} <span className="ml-req">*</span></label>
                  <input className="ml-input" value={form.title || ''} onChange={e => setForm(f => ({...f, title: e.target.value}))} />
                </div>
                <div className="ml-field">
                  <label>{t('MetadataLibrary.category')}</label>
                  <select className="ml-input" value={form.category || ''} onChange={e => setForm(f => ({...f, category: e.target.value}))}>
                    <option value="">{t('MetadataLibrary.selectOption')}</option>
                    {[['Policy','categoryPolicy'],['Procedure','categoryProcedure'],['Standard','categoryStandard'],['Guideline','categoryGuideline'],['Template','categoryTemplate'],['Evidence','categoryEvidence']].map(([c,tk]) => (
                      <option key={c} value={c}>{t(`MetadataLibrary.${tk}`)}</option>
                    ))}
                  </select>
                </div>
                <div className="ml-field">
                  <label>{t('MetadataLibrary.documentId')}</label>
                  <input className="ml-input" value={form.documentId || ''} onChange={e => setForm(f => ({...f, documentId: e.target.value}))} />
                </div>
              </div>
              <div className="ml-field" style={{marginTop:12}}>
                <label>{t('MetadataLibrary.description')}</label>
                <textarea className="ml-input ml-textarea" rows={3}
                  value={form.description || ''} onChange={e => setForm(f => ({...f, description: e.target.value}))} />
              </div>
              <p className="ml-field-note" style={{marginTop:8}}>
                <i className="fas fa-info-circle" /> {t('MetadataLibrary.documentChangesNote')}
              </p>
            </div>
            <div className="ml-modal-footer">
              <button className="ml-btn-secondary" onClick={() => setModal(null)}>{t('MetadataLibrary.cancel')}</button>
              <button className="ml-btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><i className="fas fa-spinner fa-spin" /> {t('MetadataLibrary.saving')}</> : <><i className="fas fa-check" /> {t('MetadataLibrary.save')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {delTarget && (
        <ConfirmDeleteModal item={delTarget} label={t('MetadataLibrary.document')}
          onConfirm={handleDelete} onCancel={() => setDelTarget(null)} />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   ROOT COMPONENT
═══════════════════════════════════════════════════════ */
export default function MetadataLibrary() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('frameworks');

  const TABS = [
    { key: 'frameworks', label: t('MetadataLibrary.tabFrameworks'), icon: 'fa-sitemap' },
    { key: 'controls',   label: t('MetadataLibrary.tabControls'),   icon: 'fa-shield-alt' },
    { key: 'tests',      label: t('MetadataLibrary.tabTests'),      icon: 'fa-flask' },
    { key: 'policies',   label: t('MetadataLibrary.tabPolicies'),   icon: 'fa-file-signature' },
    { key: 'documents',  label: t('MetadataLibrary.tabDocuments'),  icon: 'fa-file-alt' },
  ];

  return (
    <div className="ml-page">
      <div className="ml-header">
        <div>
          <h2 className="ml-title">{t('MetadataLibrary.pageTitle')}</h2>
          <p className="ml-subtitle">
            {t('MetadataLibrary.pageSubtitle')}
          </p>
        </div>
      </div>

      <div className="ml-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`ml-tab${activeTab === t.key ? ' ml-tab-active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            <i className={`fas ${t.icon}`} /> {t.label}
          </button>
        ))}
      </div>

      <div className="ml-tab-content">
        {activeTab === 'frameworks' && <FrameworksTab />}
        {activeTab === 'controls'   && <ControlsTab />}
        {activeTab === 'tests'      && <TestsTab />}
        {activeTab === 'policies'   && <PoliciesTab />}
        {activeTab === 'documents'  && <DocumentsTab />}
      </div>
    </div>
  );
}
